import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  SPAWN_Y,
  type ActivePiece,
  type Board,
  type PieceId,
  type Rotation,
  type RotationDirection,
} from "./types";
import { INPUT_SAMPLE_TICKS, LegacyGame, clearCompletedLines } from "./engine";
import { canPlace, cloneBoard, getPoseCells } from "./poses";

export type CpuDifficulty = "easy" | "normal" | "hard";

export type CpuPlacement = {
  rotation: Rotation;
  x: number;
  y: number;
  clearedLines: number;
  score: number;
};

type BoardMetrics = {
  aggregateHeight: number;
  bumpiness: number;
  holes: number;
  maxHeight: number;
  wells: number;
};

const DIFFICULTY_WEIGHTS: Readonly<Record<CpuDifficulty, Readonly<{
  lineClear: number;
  aggregateHeight: number;
  holes: number;
  bumpiness: number;
  maxHeight: number;
  wells: number;
}>>> = {
  easy: {
    lineClear: 11,
    aggregateHeight: 0.34,
    holes: 5.2,
    bumpiness: 0.48,
    maxHeight: 0.2,
    wells: 0.22,
  },
  normal: {
    lineClear: 18,
    aggregateHeight: 0.45,
    holes: 8,
    bumpiness: 0.65,
    maxHeight: 0.35,
    wells: 0.7,
  },
  hard: {
    lineClear: 23,
    aggregateHeight: 0.52,
    holes: 10,
    bumpiness: 0.8,
    maxHeight: 0.48,
    wells: 0.95,
  },
};

function columnMetrics(board: readonly (readonly number[])[]): BoardMetrics {
  const heights: number[] = [];
  let holes = 0;

  for (let x = 0; x < BOARD_WIDTH; x += 1) {
    let firstFilledRow: number = BOARD_HEIGHT;
    for (let y = 0; y < BOARD_HEIGHT; y += 1) {
      if (board[y]?.[x]) {
        firstFilledRow = y;
        break;
      }
    }

    const height = firstFilledRow === BOARD_HEIGHT ? 0 : BOARD_HEIGHT - firstFilledRow;
    heights.push(height);
    if (firstFilledRow < BOARD_HEIGHT) {
      for (let y = firstFilledRow + 1; y < BOARD_HEIGHT; y += 1) {
        if (!board[y]?.[x]) holes += 1;
      }
    }
  }

  let bumpiness = 0;
  for (let x = 1; x < heights.length; x += 1) bumpiness += Math.abs(heights[x] - heights[x - 1]);

  let wells = 0;
  for (let x = 0; x < heights.length; x += 1) {
    const left = x === 0 ? heights[x] + 1 : heights[x - 1];
    const right = x === heights.length - 1 ? heights[x] + 1 : heights[x + 1];
    const depth = Math.min(left, right) - heights[x];
    if (depth > 0) wells += (depth * (depth + 1)) / 2;
  }

  return {
    aggregateHeight: heights.reduce((sum, height) => sum + height, 0),
    bumpiness,
    holes,
    maxHeight: Math.max(...heights),
    wells,
  };
}

function boardScore(board: Board, clearedLines: number, difficulty: CpuDifficulty): number {
  const metrics = columnMetrics(board);
  const weights = DIFFICULTY_WEIGHTS[difficulty];
  return (
    clearedLines * weights.lineClear
    - metrics.aggregateHeight * weights.aggregateHeight
    - metrics.holes * weights.holes
    - metrics.bumpiness * weights.bumpiness
    - metrics.maxHeight * weights.maxHeight
    - metrics.wells * weights.wells
  );
}

function dropY(board: Board, active: ActivePiece): number | null {
  if (!canPlace(board, active)) return null;
  let y = active.y;
  while (canPlace(board, { ...active, y: y + 1 })) y += 1;
  return y;
}

function boardAfterPlacement(board: Board, active: ActivePiece): { board: Board; clearedLines: number } {
  const placed = cloneBoard(board);
  for (const { x, y } of getPoseCells(active)) placed[y - 1][x - 1] = active.piece;
  return clearCompletedLines(placed);
}

function nextPieceLookahead(board: Board, piece: PieceId, difficulty: CpuDifficulty): number {
  let best = Number.NEGATIVE_INFINITY;
  for (let rotation = 0; rotation < 4; rotation += 1) {
    for (let x = -2; x <= BOARD_WIDTH + 2; x += 1) {
      const candidate: ActivePiece = { piece, rotation: rotation as Rotation, x, y: SPAWN_Y };
      const y = dropY(board, candidate);
      if (y === null) continue;
      const result = boardAfterPlacement(board, { ...candidate, y });
      best = Math.max(best, boardScore(result.board, result.clearedLines, difficulty));
    }
  }
  return best === Number.NEGATIVE_INFINITY ? 0 : best;
}

/**
 * Selects a legal landing position using the same 4x4 legacy poses as the
 * actual game. The optional rotation filter is used when a crowded stack
 * prevents a safe rotation path during live play.
 */
export function chooseCpuPlacement(
  board: Board,
  active: ActivePiece,
  difficulty: CpuDifficulty = "normal",
  nextPiece?: PieceId,
  rotationFilter?: readonly Rotation[],
): CpuPlacement {
  const rotations = rotationFilter ?? ([0, 1, 2, 3] as const);
  let best: CpuPlacement | undefined;

  for (const rotation of rotations) {
    for (let x = -2; x <= BOARD_WIDTH + 2; x += 1) {
      const candidate = { ...active, rotation, x, y: active.y };
      const y = dropY(board, candidate);
      if (y === null) continue;
      const result = boardAfterPlacement(board, { ...candidate, y });
      let score = boardScore(result.board, result.clearedLines, difficulty);

      if (difficulty === "hard" && nextPiece !== undefined) {
        score += nextPieceLookahead(result.board, nextPiece, difficulty) * 0.18;
      }

      // Stable tie-breakers make the CPU look deliberate instead of jittery.
      score -= Math.abs(x - 4) * 0.001;
      if (
        best === undefined
        || score > best.score
        || (score === best.score && y > best.y)
      ) {
        best = { rotation, x, y, clearedLines: result.clearedLines, score };
      }
    }
  }

  if (best) return best;
  return {
    rotation: active.rotation,
    x: active.x,
    y: active.y,
    clearedLines: 0,
    score: Number.NEGATIVE_INFINITY,
  };
}

function nextSampleTick(game: LegacyGame): number {
  const elapsed = Math.max(0, game.tick - game.startTick);
  return game.startTick + (Math.floor(elapsed / INPUT_SAMPLE_TICKS) + 1) * INPUT_SAMPLE_TICKS;
}

function rotationSteps(from: Rotation, to: Rotation): { direction: RotationDirection; count: number } {
  const cw = (to - from + 4) % 4;
  const ccw = (from - to + 4) % 4;
  return cw <= ccw ? { direction: "cw", count: cw } : { direction: "ccw", count: ccw };
}

/**
 * Drives a LegacyGame with short, sample-aligned pulses. That keeps CPU input
 * subject to the same rotation latch and horizontal repeat rules as a player.
 */
export class CpuPilot {
  private readonly difficulty: CpuDifficulty;
  private plannedBlockIndex = -1;
  private placement: CpuPlacement | undefined;
  private rotationDirection: RotationDirection = "cw";
  private rotationsRemaining = 0;
  private rotationHeld = false;
  private rotationAtPulse: Rotation = 0;
  private rotationReleaseTick = 0;
  private rotationReadyTick = 0;
  private rotationFailures = 0;
  private horizontalHeld = false;
  private horizontalDirection: "left" | "right" = "right";
  private horizontalReleaseTick = 0;
  private horizontalReadyTick = 0;
  private horizontalPulseStartX = 0;
  private horizontalFailures = 0;
  private softDropStarted = false;

  constructor(difficulty: CpuDifficulty = "normal") {
    this.difficulty = difficulty;
  }

  reset(): void {
    this.plannedBlockIndex = -1;
    this.placement = undefined;
    this.rotationsRemaining = 0;
    this.rotationHeld = false;
    this.horizontalHeld = false;
    this.softDropStarted = false;
  }

  private releaseAll(game: LegacyGame): void {
    game.setInputs({
      left: false,
      right: false,
      softDrop: false,
      hardDrop: false,
      rotateCW: false,
      rotateCCW: false,
    });
    this.rotationHeld = false;
    this.horizontalHeld = false;
    this.softDropStarted = false;
  }

  private replan(game: LegacyGame, rotationFilter?: readonly Rotation[]): void {
    this.placement = chooseCpuPlacement(
      game.board,
      game.active,
      this.difficulty,
      game.nextPiece,
      rotationFilter,
    );
    const rotationPlan = rotationSteps(game.active.rotation, this.placement.rotation);
    this.rotationDirection = rotationPlan.direction;
    this.rotationsRemaining = rotationPlan.count;
    this.rotationFailures = 0;
    this.horizontalFailures = 0;
    this.rotationReadyTick = game.tick;
    this.horizontalReadyTick = game.tick;
  }

  sync(game: LegacyGame): void {
    if (game.topOut) return;

    if (game.blockIndex !== this.plannedBlockIndex) {
      this.releaseAll(game);
      this.plannedBlockIndex = game.blockIndex;
      this.replan(game);
    }

    if (!this.placement) return;

    if (this.rotationHeld) {
      const rotationChanged = game.active.rotation !== this.rotationAtPulse;
      if (rotationChanged || game.tick >= this.rotationReleaseTick) {
        game.setInput(this.rotationDirection === "cw" ? "rotateCW" : "rotateCCW", false);
        this.rotationHeld = false;
        this.rotationReadyTick = nextSampleTick(game) + 1;
        if (rotationChanged) {
          this.rotationFailures = 0;
          const next = rotationSteps(game.active.rotation, this.placement.rotation);
          this.rotationDirection = next.direction;
          this.rotationsRemaining = next.count;
        } else {
          this.rotationFailures += 1;
          if (this.rotationFailures >= 3) {
            this.replan(game, [game.active.rotation]);
          }
        }
      }
    }

    if (this.rotationsRemaining > 0) {
      if (game.tick >= this.rotationReadyTick && !this.rotationHeld) {
        const control = this.rotationDirection === "cw" ? "rotateCW" : "rotateCCW";
        game.setInput(control, true);
        this.rotationHeld = true;
        this.rotationAtPulse = game.active.rotation;
        this.rotationReleaseTick = nextSampleTick(game) + 1;
      }
      return;
    }

    if (this.horizontalHeld) {
      if (game.active.x === this.placement.x || game.tick >= this.horizontalReleaseTick) {
        game.setInput(this.horizontalDirection, false);
        this.horizontalHeld = false;
        this.horizontalReadyTick = nextSampleTick(game) + 1;
        if (game.active.x === this.horizontalPulseStartX) this.horizontalFailures += 1;
        else this.horizontalFailures = 0;
        if (this.horizontalFailures >= 3) this.replan(game, [game.active.rotation]);
      }
    }

    if (game.active.x !== this.placement.x) {
      if (!this.horizontalHeld && game.tick >= this.horizontalReadyTick) {
        this.horizontalDirection = game.active.x < this.placement.x ? "right" : "left";
        game.setInput(this.horizontalDirection, true);
        this.horizontalHeld = true;
        this.horizontalPulseStartX = game.active.x;
        this.horizontalReleaseTick = nextSampleTick(game) + 1;
      }
      return;
    }

    if (!this.softDropStarted) {
      game.setInput("softDrop", true);
      this.softDropStarted = true;
    }
  }
}
